import { getLocalTimeZone, today } from '@internationalized/date'
import {
  addMonths,
  differenceInCalendarMonths,
  format,
  getDate,
  isAfter,
  isBefore,
  lastDayOfMonth,
  parseISO,
  setDate,
} from 'date-fns'
import { t } from 'i18next'

import type { IGerenciadorPosConclusao } from '@/domain/simulador/gerenciador-pos-conclusao.interface'
import { PrazoEntregaSimulacao } from '@/domain/simulador/prazo-entrega-simulacao'
import type { TGrupoMensais } from '@/utils/calculadora-valores-totais/processador-valores-decimal'
import { constantsSimulacao } from '@/utils/constants-simulacao'

export class GerenciadorPosConclusaoBase implements IGerenciadorPosConclusao<TGrupoMensais> {
  private clamp(data: Date, diaAlvo: number): Date {
    const ultimoDiaMes = getDate(lastDayOfMonth(data))
    const diaValido = Math.min(diaAlvo, ultimoDiaMes)
    return setDate(data, diaValido)
  }

  protected criar(
    dataInicio: string = today(getLocalTimeZone()).toString(),
    dataFim: string = today(getLocalTimeZone()).toString(),
    valor = 0,
  ): TGrupoMensais {
    return {
      dataFim,
      dataInicio,
      quantidade: differenceInCalendarMonths(parseISO(dataFim), parseISO(dataInicio)) + 1,
      valor,
    }
  }

  deveGerarPosConclusao(
    dataPrevistaEntregaBloco: string,
    dataSimulacao: string,
  ): { sucesso: boolean; mensagem?: string } {
    const prazoEntrega = new PrazoEntregaSimulacao(dataSimulacao, dataPrevistaEntregaBloco)
    if (prazoEntrega.isEmpreendimentoEntregue) {
      return {
        sucesso: false,
        mensagem: t('simulador:components.pos_conclusao.erros.empreendimento_entregue'),
      }
    }

    const isMesesParaEntregaMaiorQueLimite =
      prazoEntrega.prazoEntregaEmMeses >= constantsSimulacao.LIMITE_MAXIMO_NOVA_FORMA_PAGAMENTO_SEM_POS_CONCLUSAO
    const isMesesParaEntregaMenorQueLimite =
      prazoEntrega.prazoEntregaEmMeses <= constantsSimulacao.LIMITE_MINIMO_NOVA_FORMA_PAGAMENTO

    if (isMesesParaEntregaMaiorQueLimite) {
      return {
        sucesso: false,
        mensagem: t('simulador:components.pos_conclusao.erros.entrega_excede_limite'),
      }
    }
    if (isMesesParaEntregaMenorQueLimite) {
      return {
        sucesso: false,
        mensagem: t('simulador:components.pos_conclusao.erros.entrega_inferior_limite'),
      }
    }
    return { sucesso: true }
  }

  criarGrupo(valor: number, dataSaldo: string, dataPrevistaEntregaBloco: string, dataSimulacao: string): TGrupoMensais {
    const prazoEntrega = new PrazoEntregaSimulacao(dataSimulacao, dataPrevistaEntregaBloco)

    const quantidadeParcelas = constantsSimulacao.LIMITE_MAXIMO_NOVA_FORMA_PAGAMENTO - prazoEntrega.prazoEntregaEmMeses
    const dataInicio = format(setDate(addMonths(parseISO(dataSaldo), 1), 10), 'yyyy-MM-dd')
    const dataFim = format(setDate(addMonths(parseISO(dataSaldo), quantidadeParcelas), 10), 'yyyy-MM-dd')

    return { valor, dataInicio, dataFim, quantidade: quantidadeParcelas }
  }

  calcularAjustePorQuantidade(grupo: TGrupoMensais, novaQuantidade: number): TGrupoMensais {
    if (!grupo?.dataInicio) return grupo

    const dataInicioParsed = parseISO(grupo.dataInicio)
    const diaAlvo = getDate(dataInicioParsed)
    const novaDataFim = this.clamp(addMonths(dataInicioParsed, novaQuantidade - 1), diaAlvo)

    if (isBefore(novaDataFim, dataInicioParsed)) {
      return {
        ...grupo,
        dataFim: grupo.dataInicio,
        quantidade: 1,
      }
    }

    return {
      ...grupo,
      dataFim: format(novaDataFim, 'yyyy-MM-dd'),
      quantidade: novaQuantidade,
    }
  }

  calcularAjustePorDataInicio(grupo: TGrupoMensais, novaDataInicio: string) {
    if (!grupo.dataFim) return grupo

    const dataFimParsed = parseISO(grupo.dataFim)
    const dataInicioParsed = parseISO(novaDataInicio)
    const diaAlvo = getDate(dataInicioParsed)
    const novaDataFim = this.clamp(dataFimParsed, diaAlvo)

    if (isBefore(novaDataFim, dataInicioParsed)) {
      return {
        valor: grupo.valor,
        dataInicio: novaDataInicio,
        dataFim: novaDataInicio,
        quantidade: 1,
      }
    }

    const novaQuantidade = differenceInCalendarMonths(novaDataFim, dataInicioParsed) + 1

    return {
      valor: grupo.valor,
      dataInicio: novaDataInicio,
      dataFim: format(novaDataFim, 'yyyy-MM-dd'),
      quantidade: Math.abs(novaQuantidade),
    }
  }

  calcularAjustePorDataFim(grupo: TGrupoMensais, novaDataFim: string) {
    if (!grupo.dataInicio) return grupo

    const dataInicioParsed = parseISO(grupo.dataInicio)
    const dataFimParsed = parseISO(novaDataFim)
    const diaAlvo = getDate(dataFimParsed)
    const novaDataInicio = this.clamp(dataInicioParsed, diaAlvo)

    if (isAfter(novaDataInicio, dataFimParsed)) {
      return {
        valor: grupo.valor,
        dataInicio: novaDataFim,
        dataFim: novaDataFim,
        quantidade: 1,
      }
    }

    const novaQuantidade = differenceInCalendarMonths(dataFimParsed, novaDataInicio) + 1

    return {
      valor: grupo.valor,
      dataInicio: format(novaDataInicio, 'yyyy-MM-dd'),
      dataFim: format(dataFimParsed, 'yyyy-MM-dd'),
      quantidade: novaQuantidade,
    }
  }
}
